import type {
  AuthMeResponse,
  CheckSlugResponse,
  CreateItemRequest,
  CreateRegistryRequest,
  FromUrlResponse,
  Item,
  Registry,
  UpdateRegistryRequest,
  UpdateItemRequest,
} from '@lds/shared'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api'

class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`HTTP ${status}: ${JSON.stringify(body)}`)
  }
}

async function request<T>(path: string, init: Parameters<typeof fetch>[1] = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin).toString()
  const res = await fetch(url, { ...(init ?? {}), credentials: 'include' })
  if (!res.ok) {
    let body: unknown = null
    const ct = res.headers.get('content-type') ?? ''
    if (ct.includes('application/json')) {
      try {
        body = await res.json()
      } catch {
        body = await res.text()
      }
    } else {
      body = await res.text()
    }
    if (body === '') body = null
    throw new ApiError(res.status, body)
  }
  if (res.status === 204) return undefined as T
  const ct = res.headers.get('content-type') ?? ''
  const text = await res.text()
  if (text === '') return undefined as T
  if (ct.includes('application/json')) return JSON.parse(text) as T
  return text as T
}

export const api = {
  authMe: () => request<AuthMeResponse>('/auth/me'),
  authLogout: () => request<void>('/auth/logout', { method: 'POST' }),
  authMagicLinkRequest: (email: string, next?: string) =>
    request<void>('/auth/magic-link/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(next ? { email, next } : { email }),
    }),
  raw: request,
}

export const registryApi = {
  list: () =>
    request<Array<{ id: string; slug: string; status: 'draft' | 'published'; title: string }>>(
      '/me/registries',
    ),
  create: (body: CreateRegistryRequest) =>
    request<{ id: string; slug: string }>('/me/registries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  checkSlug: (slug: string) =>
    request<CheckSlugResponse>(`/me/registries/check-slug?slug=${encodeURIComponent(slug)}`),
  get: (rid: string) => request<Registry>(`/me/registries/${rid}`),
  publish: (rid: string) => request<void>(`/me/registries/${rid}/publish`, { method: 'POST' }),
  unpublish: (rid: string) => request<void>(`/me/registries/${rid}/unpublish`, { method: 'POST' }),
  delete: (rid: string) => request<void>(`/me/registries/${rid}`, { method: 'DELETE' }),
  update: (rid: string, body: UpdateRegistryRequest) =>
    request<void>(`/me/registries/${rid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  inviteCoOwner: (rid: string, email: string) =>
    request<{ id: string }>(`/me/registries/${rid}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),
  cancelInvite: (rid: string, invId: string) =>
    request<void>(`/me/registries/${rid}/invites/${invId}`, { method: 'DELETE' }),
  removeMember: (rid: string, userId: string) =>
    request<void>(`/me/registries/${rid}/members/${userId}/remove`, { method: 'POST' }),
}

export const itemApi = {
  list: (rid: string) => request<Item[]>(`/me/registries/${rid}/items`),
  create: (rid: string, body: CreateItemRequest) =>
    request<{ id: string }>(`/me/registries/${rid}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  update: (rid: string, iid: string, body: UpdateItemRequest) =>
    request<void>(`/me/registries/${rid}/items/${iid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  delete: (rid: string, iid: string) =>
    request<void>(`/me/registries/${rid}/items/${iid}`, { method: 'DELETE' }),
  reorder: (rid: string, orderedIds: string[]) =>
    request<void>(`/me/registries/${rid}/items/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds }),
    }),
  fromUrl: (rid: string, url: string) =>
    request<FromUrlResponse>(`/me/registries/${rid}/items/from-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    }),
}

export const imageApi = {
  upload: async (file: File, registryId?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (registryId) fd.append('registry_id', registryId)
    return request<{ id: string; url: string }>('/images', { method: 'POST', body: fd })
  },
  fromUrl: (url: string, registryId?: string) =>
    request<{ id: string; url: string }>('/images/from-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, registry_id: registryId }),
    }),
}

export const accountApi = {
  updateDisplayName: (displayName: string) =>
    request<void>('/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName }),
    }),
  delete: () => request<void>('/me', { method: 'DELETE' }),
  logoutEverywhere: () => request<void>('/auth/logout-everywhere', { method: 'POST' }),
  listTokens: () =>
    request<
      Array<{
        id: string
        name: string
        scopes: string[]
        lastUsedAt: number | null
        createdAt: number
      }>
    >('/me/api-tokens'),
  createToken: (name: string, scopes: string[]) =>
    request<{ id: string; name: string; scopes: string[]; token: string }>('/me/api-tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, scopes }),
    }),
  revokeToken: (tid: string) => request<void>(`/me/api-tokens/${tid}`, { method: 'DELETE' }),
  bookmarkletInstallHref: (tokenId: string) =>
    request<string>(`/me/bookmarklet/install?token_id=${encodeURIComponent(tokenId)}`),
}

export const invitePublicApi = {
  details: (token: string) =>
    request<{ registryTitle: string; inviterName: string; inviteEmail: string }>(
      `/invites/${token}`,
    ),
  accept: (token: string) =>
    request<{ ok: true; registryId: string }>(`/invites/${token}/accept`, { method: 'POST' }),
}

type PublicCatalog = {
  registry: {
    title: string
    dueDate: string | null
    welcomeMessage: string | null
    coverImageUrl: string | null
    theme: 'editorial' | 'minimal' | 'playful'
    accentColor: string | null
    paymentMethods: {
      iban: string | null
      bic: string | null
      ibanHolder: string | null
      lydiaPhone: string | null
      weroPhone: string | null
      paypalEmail: string | null
    }
  }
  items: Array<{
    id: string
    title: string
    description: string | null
    priceCents: number
    imageUrl: string | null
    externalUrl: string | null
    outUrl: string
    offered: boolean
    alreadyGivenCents: number
    remainingCents: number
    complete: boolean
  }>
}

type PublicContributeRequest = {
  itemId: string
  guestName: string
  guestEmail: string
  amountCents: number
  message?: string | null
  paymentMethod: 'iban' | 'lydia' | 'wero' | 'paypal' | 'direct'
  turnstileToken?: string
}

type PublicContributeResponse =
  | {
      ok: true
      participationId: string
      alreadyGivenCents: number
      remainingCents: number
      complete: boolean
    }
  | { error: 'insufficient_remaining'; remainingCents: number }
  | { error: 'turnstile_failed' }
  | { error: 'item_offered' }

export const publicApi = {
  catalog: (slug: string) => request<PublicCatalog>(`/public/r/${encodeURIComponent(slug)}`),
  contribute: (slug: string, body: PublicContributeRequest) =>
    request<PublicContributeResponse>(`/public/r/${encodeURIComponent(slug)}/participations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
}

export { ApiError }
